import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { isSuperAdmin } from '@/hooks/useAuth';
import { useBrand } from '@/hooks/useBrand';
import { supabase } from '@/lib/supabase';
import { buildSubdomain } from '@/lib/domain';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';
import CurrencySwitcher from '@/components/feature/CurrencySwitcher';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { appendCurrencyEquivalence } from '@/lib/currency';
import AccountQrModal from '@/components/feature/AccountQrModal';
import QrScannerModal from '@/components/feature/QrScannerModal';
import MobileBottomNav from '@/components/feature/MobileBottomNav';
import DomainAlertBanner from '@/components/feature/DomainAlertBanner';
import useDomainMonitor from '@/hooks/useDomainMonitor';
import { buildAccountQrData, parseAccountQrData } from '@/lib/accountQr';
import { avatarOr } from '@/lib/defaultImages';
import { usePwa } from '@/components/feature/PwaProvider';
import ErrorBoundary from '@/components/feature/ErrorBoundary';
import SortableNavApp from '@/components/feature/SortableNavApp';
import { RESTAURANT_TYPE_COMPTE, TYPE_COMPTE_CLIENT } from '@/lib/accountType';
import { isMarketplaceAccount } from '@/lib/marketplace';
import PlatformVersionBadge from '@/components/feature/PlatformVersionBadge';
import PermissionGuard from '@/components/feature/PermissionGuard';
import GlobalSearchBar from '@/components/feature/GlobalSearchBar';
import { usePermissions } from '@/hooks/usePermissions';
import { requiredPermissionsForPath, roleMeta } from '@/lib/corePermissions';
import { getCommerceId } from '@/lib/ownership';
import { getOrganizationByCommerce } from '@/lib/zifekCore';
import { listCoreNotifications, markNotificationRead, type CoreNotification } from '@/lib/coreNotification';
import { playOrderSound } from '@/lib/orderSound';
import AiClientThreadModal from '@/pages/dashboard/socle/components/AiClientThreadModal';
import AssistantDock from '@/components/feature/AssistantDock';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';

interface AppLink {
  nompage: string;
  label: string;
  icon: string;
}

interface Notification {
  id: number;
  user_id: number;
  sender_id: number;
  publication_id: number;
  type: string;
  message: string;
  is_read: number;
  created_at: string;
}

interface BaseNavItem {
  icon: string;
  label: string;
  tkey?: string;
  path: string;
  appPage?: string;
}

const BASE_NAV_ITEMS: BaseNavItem[] = [
  { icon: 'ri-dashboard-line', label: 'Tableau de bord', tkey: 'dash.dashboard', path: '/dashboard' },
  { icon: 'ri-sun-line', label: 'Mon activité du jour', path: '/dashboard/activite' },
  { icon: 'ri-shopping-bag-3-line', label: 'Produits', tkey: 'dash.products', path: '/dashboard/products' },
  { icon: 'ri-archive-line', label: 'Gestion de stock', path: '/dashboard/gestion-stock' },
  { icon: 'ri-shopping-cart-2-line', label: 'Ma caisse', tkey: 'dash.caisse', path: '/dashboard/caisse', appPage: 'caisse' },
  { icon: 'ri-service-line', label: 'Services', tkey: 'dash.services', path: '/dashboard/services' },
  { icon: 'ri-file-list-3-line', label: 'Commandes', tkey: 'dash.orders', path: '/dashboard/orders' },
  { icon: 'ri-user-line', label: 'Clients', tkey: 'dash.customers', path: '/dashboard/customers' },
  { icon: 'ri-chat-3-line', label: 'Messages', tkey: 'dash.messages', path: '/dashboard/messages' },
  { icon: 'ri-book-2-line', label: 'Crédit', tkey: 'dash.credit', path: '/dashboard/credit' },
  { icon: 'ri-bank-card-line', label: 'Paiements', tkey: 'dash.payments', path: '/dashboard/payments' },
  { icon: 'ri-hand-heart-line', label: 'Besoins', path: '/dashboard/besoins' },
  { icon: 'ri-megaphone-line', label: 'Missions', path: '/dashboard/missions' },
  { icon: 'ri-wallet-3-line', label: 'Portefeuille', tkey: 'dash.wallet', path: '/dashboard/wallet' },
  { icon: 'ri-share-circle-line', label: 'Revendeur', path: '/dashboard/revendeur' },
  { icon: 'ri-vip-crown-line', label: 'Abonnement', path: '/dashboard/abonnement' },
  { icon: 'ri-global-line', label: 'Mon siteweb', tkey: 'dash.my_website', path: '/dashboard/mon-siteweb' },
  { icon: 'ri-article-line', label: 'Blog', path: '/dashboard/blog' },
  { icon: 'ri-calendar-event-line', label: 'Événements', path: '/dashboard/events' },
  { icon: 'ri-bar-chart-box-line', label: 'Statistiques MyLinks', path: '/dashboard/mylinks-stats' },
  { icon: 'ri-organization-chart', label: 'Mon entreprise', path: '/dashboard/core' },
  { icon: 'ri-user-settings-line', label: "Gestion d'utilisateurs", path: '/dashboard/users' },
  { icon: 'ri-history-line', label: "Journal d'activité", path: '/dashboard/journal' },
  { icon: 'ri-stack-line', label: 'Socle central', path: '/dashboard/socle' },
  { icon: 'ri-image-line', label: 'Médias', tkey: 'dash.media', path: '/dashboard/media' },
  { icon: 'ri-file-text-line', label: 'Documents', tkey: 'dash.documents', path: '/dashboard/documents' },
  { icon: 'ri-database-2-line', label: 'Stockage', tkey: 'dash.storage', path: '/dashboard/storage' },
  { icon: 'ri-line-chart-line', label: 'Statistiques', tkey: 'dash.analytics', path: '/dashboard/analytics' },
  { icon: 'ri-apps-2-line', label: 'AppStore', tkey: 'dash.appstore', path: '/dashboard/appstore' },
  { icon: 'ri-toggle-line', label: 'Mes Apps', tkey: 'dash.apps', path: '/dashboard/apps' },
  { icon: 'ri-palette-line', label: 'ThemeStore', tkey: 'dash.themestore', path: '/dashboard/themestore' },
  { icon: 'ri-settings-3-line', label: 'Paramètres', tkey: 'dash.settings', path: '/dashboard/settings' },
];

/**
 * Regroupement du menu latéral.
 * Objectif : un commerçant qui arrive doit voir peu de lignes. Les fonctions
 * avancées sont rangées dans des sections dépliables, ouvertes uniquement
 * quand on en a besoin (ou quand une page de la section est active).
 */
const NAV_GROUPS: { key: string; label: string; icon: string; paths: string[] }[] = [
  {
    key: 'vente',
    label: 'Vendre',
    icon: 'ri-store-3-line',
    paths: [
      '/dashboard/products',
      '/dashboard/gestion-stock',
      '/dashboard/caisse',
      '/dashboard/services',
      '/dashboard/orders',
      '/dashboard/customers',
      '/dashboard/credit',
      '/dashboard/restaurant',
      '/dashboard/marketplace',
    ],
  },
  {
    key: 'communication',
    label: 'Clients & communication',
    icon: 'ri-megaphone-line',
    paths: ['/dashboard/messages', '/dashboard/missions', '/dashboard/besoins', '/dashboard/events'],
  },
  {
    key: 'finance',
    label: 'Argent',
    icon: 'ri-wallet-3-line',
    paths: ['/dashboard/wallet', '/dashboard/payments', '/dashboard/revendeur', '/dashboard/abonnement'],
  },
  {
    key: 'site',
    label: 'Mon site internet',
    icon: 'ri-global-line',
    paths: ['/dashboard/mon-siteweb', '/dashboard/blog', '/dashboard/themestore', '/dashboard/mylinks-stats'],
  },
  {
    key: 'croissance',
    label: 'Croissance & outils',
    icon: 'ri-line-chart-line',
    paths: ['/dashboard/analytics', '/dashboard/appstore', '/dashboard/apps'],
  },
  {
    key: 'entreprise',
    label: 'Mon entreprise',
    icon: 'ri-building-4-line',
    paths: ['/dashboard/core', '/dashboard/users', '/dashboard/journal'],
  },
  {
    key: 'systeme',
    label: 'Contenus & réglages',
    icon: 'ri-settings-3-line',
    paths: ['/dashboard/media', '/dashboard/documents', '/dashboard/storage', '/dashboard/socle', '/dashboard/settings'],
  },
];

function groupKeyForPath(path: string): string | null {
  const found = NAV_GROUPS.find((g) => g.paths.includes(path));
  return found ? found.key : null;
}

// Entrée dédiée aux commerces de restauration (affichée seulement si typecompte = 2).
const RESTAURANT_NAV_ITEM: BaseNavItem = {
  icon: 'ri-restaurant-2-line',
  label: 'Restaurant',
  path: '/dashboard/restaurant',
};

// Entrée dédiée aux comptes marketplace (mise en avant annonces + vendeurs).
const MARKETPLACE_NAV_ITEM: BaseNavItem = {
  icon: 'ri-store-3-line',
  label: 'Marketplace',
  path: '/dashboard/marketplace',
};

const INSTALLABLE_APPS: AppLink[] = [
  { nompage: 'analytics-advanced', label: 'Analytics Avancés', icon: 'ri-line-chart-line' },
  { nompage: 'customers-manager', label: 'Gestion Clients', icon: 'ri-user-settings-line' },
  { nompage: 'whatsapp-button', label: 'Bouton WhatsApp', icon: 'ri-whatsapp-line' },
  { nompage: 'ai-shopper', label: 'AI Shopper', icon: 'ri-robot-2-line' },
  { nompage: 'customer-support', label: 'Service Client', icon: 'ri-customer-service-2-line' },
  { nompage: 'facebook-pixel', label: 'Facebook Pixel', icon: 'ri-facebook-circle-line' },
  { nompage: 'ia-reporting', label: 'Analyse & Reporting IA', icon: 'ri-brain-line' },
  { nompage: 'ma-fidelite', label: 'Ma Fidélité', icon: 'ri-heart-line' },
  { nompage: 'fidelite-recompenses', label: 'Fidélité & Récompenses', icon: 'ri-vip-crown-line' },
  { nompage: 'mon-assistant', label: 'Mon Assistant', icon: 'ri-robot-2-line' },
  { nompage: 'paypal', label: 'PayPal', icon: 'ri-paypal-line' },
  { nompage: 'file-manager', label: 'File Manager', icon: 'ri-folder-line' },
  { nompage: 'forms', label: 'Forms', icon: 'ri-survey-line' },
  { nompage: 'zcalendar', label: 'ZCalendar', icon: 'ri-calendar-line' },
  { nompage: 'zifekbi', label: 'ZifekBI', icon: 'ri-bar-chart-box-line' },
  { nompage: 'gestionpro', label: 'GESTIONPRO', icon: 'ri-projector-line' },
  { nompage: 'zcall', label: 'ZCall', icon: 'ri-vidicon-line' },
  { nompage: 'formation', label: 'Formation', icon: 'ri-graduation-cap-line' },
  { nompage: 'hebergement', label: 'Hébergement', icon: 'ri-hotel-line' },
  { nompage: 'finance', label: 'Zifek Finance', icon: 'ri-funds-line' },
  { nompage: 'content-studio', label: 'Content Studio', icon: 'ri-quill-pen-ai-line' },
  { nompage: 'zstudio', label: 'ZStudio', icon: 'ri-magic-line' },
];

type PreviewDevice = 'mobile' | 'tablet' | 'desktop';

const DEVICE_SIZES: Record<PreviewDevice, { width: string; height: string; label: string; icon: string }> = {
  mobile: { width: '375px', height: '667px', label: 'Mobile', icon: 'ri-smartphone-line' },
  tablet: { width: '768px', height: '900px', label: 'Tablette', icon: 'ri-tablet-line' },
  desktop: { width: '100%', height: '100%', label: 'Desktop', icon: 'ri-computer-line' },
};

function cleanDisplayUrl(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

const NOTIF_TYPE_META: Record<string, { icon: string; className: string }> = {
  order: { icon: 'ri-shopping-cart-2-line', className: 'text-primary-600 bg-primary-50' },
  payment: { icon: 'ri-bank-card-line', className: 'text-primary-600 bg-primary-50' },
  like: { icon: 'ri-heart-3-line', className: 'text-accent-600 bg-accent-50' },
  comment: { icon: 'ri-chat-3-line', className: 'text-secondary-600 bg-secondary-50' },
  follow: { icon: 'ri-user-add-line', className: 'text-accent-600 bg-accent-50' },
  message: { icon: 'ri-message-3-line', className: 'text-primary-600 bg-primary-50' },
  mention: { icon: 'ri-at-line', className: 'text-secondary-600 bg-secondary-50' },
  store_transfer: { icon: 'ri-exchange-funds-line', className: 'text-secondary-700 bg-secondary-100' },
  loyalty_points: { icon: 'ri-star-line', className: 'text-accent-700 bg-accent-100' },
  money_request: { icon: 'ri-hand-heart-line', className: 'text-primary-600 bg-primary-50' },
  booking: { icon: 'ri-calendar-check-line', className: 'text-accent-600 bg-accent-50' },
  contact: { icon: 'ri-mail-line', className: 'text-secondary-600 bg-secondary-50' },
  vendor: { icon: 'ri-store-2-line', className: 'text-primary-600 bg-primary-50' },
  stock: { icon: 'ri-error-warning-line', className: 'text-red-600 bg-red-50' },
  form: { icon: 'ri-survey-line', className: 'text-secondary-600 bg-secondary-50' },
  besoin: { icon: 'ri-hand-heart-line', className: 'text-accent-700 bg-accent-100' },
  mission: { icon: 'ri-megaphone-line', className: 'text-primary-600 bg-primary-50' },
  payment_failed: { icon: 'ri-error-warning-line', className: 'text-red-600 bg-red-50' },
  system: { icon: 'ri-information-line', className: 'text-foreground-600 bg-background-100' },
};

// Libellé court par type, affiché en pastille au-dessus du message.
const NOTIF_TYPE_LABEL: Record<string, string> = {
  order: 'Commande',
  payment: 'Paiement',
  message: 'Message',
  contact: 'Contact',
  booking: 'Réservation',
  vendor: 'Vendeur',
  store_transfer: 'Transfert',
  loyalty_points: 'Fidélité',
  stock: 'Stock',
  form: 'Formulaire',
  besoin: 'Besoin',
  mission: 'Mission',
  payment_failed: 'Paiement refusé',
};

function notifMeta(type: string) {
  return NOTIF_TYPE_META[type] || { icon: 'ri-notification-3-line', className: 'text-foreground-600 bg-background-100' };
}

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days} j`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `il y a ${weeks} sem`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Destination d'une notification : où ouvrir la commande, le message ou
 * l'entité concernée quand on clique dessus dans la cloche.
 * Retourne null quand la notification n'a pas de page cible (on se contente
 * alors de la marquer comme lue).
 */
function notificationTarget(n: Notification): string | null {
  switch (n.type) {
    case 'order':
    case 'payment':
      return n.publication_id && n.publication_id > 0
        ? `/dashboard/orders?order=${n.publication_id}`
        : '/dashboard/orders';
    case 'payment_failed':
      return '/dashboard/abonnement';
    case 'besoin':
      return '/dashboard/besoins';
    case 'mission':
      // Une notification de mission ouvre la mission concernée (candidature,
      // publication à vérifier…) au lieu de la simple liste.
      return n.publication_id && n.publication_id > 0
        ? `/dashboard/missions/${n.publication_id}`
        : '/dashboard/missions';
    case 'message':
      return '/dashboard/messages';
    case 'contact':
      return '/dashboard/apps/customer-support';
    case 'booking':
      return '/dashboard/apps/zcalendar';
    case 'stock':
      return '/dashboard/gestion-stock';
    case 'form':
      return '/dashboard/apps/forms/responses';
    case 'vendor':
      return '/dashboard/marketplace';
    case 'store_transfer':
    case 'money_request':
      return '/dashboard/wallet';
    case 'loyalty_points':
      return '/dashboard/apps/ma-fidelite';
    default:
      return null;
  }
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { brand } = useBrand();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  /** Menu latéral replié sur ordinateur : libère toute la largeur pour l'édition. */
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('zifek-sidebar-collapsed') === '1';
    } catch {
      return false;
    }
  });
  const [searchQuery, setSearchQuery] = useState('');
  /** Sections du menu dépliées (accordéons) — mémorisées d'une visite à l'autre. */
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem('zifek-nav-groups');
      if (raw) return JSON.parse(raw) as Record<string, boolean>;
    } catch {
      /* stockage indisponible */
    }
    return { vente: true };
  });

  const toggleGroup = useCallback((key: string) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem('zifek-nav-groups', JSON.stringify(next));
      } catch {
        /* stockage indisponible */
      }
      return next;
    });
  }, []);
  const [siteUrl, setSiteUrl] = useState<string>('');
  const [isCustomDomain, setIsCustomDomain] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop');
  const [installedAppPages, setInstalledAppPages] = useState<Set<string>>(new Set());
  const [appMenuItems, setAppMenuItems] = useState<AppLink[]>([]);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const [accountQrOpen, setAccountQrOpen] = useState(false);
  const [qrChooserOpen, setQrChooserOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanMsg, setScanMsg] = useState('');
  const [installInfoOpen, setInstallInfoOpen] = useState(false);
  const { installed: pwaInstalled, canInstall: pwaCanInstall, install: pwaInstall } = usePwa();
  /** Total de mois gratuits (formule + parrainage) — badge d'en-tête. */
  const [freeMonths, setFreeMonths] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const [badgeBlink, setBadgeBlink] = useState(false);
  const prevUnreadRef = useRef(0);
  const [orderToast, setOrderToast] = useState<{ id: number; client: string; total: string; currency: string } | null>(null);
  const orderToastTimer = useRef<number | null>(null);
  const [unreadContactCount, setUnreadContactCount] = useState(0);
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [stockOutCount, setStockOutCount] = useState(0);
  const [aiNotifications, setAiNotifications] = useState<CoreNotification[]>([]);
  const [aiOrg, setAiOrg] = useState<{ id: number; tenantId: number | null } | null>(null);
  const [aiThread, setAiThread] = useState<{ clientId: number; context: string | null } | null>(null);
  const { currency: displayCurrency } = useDisplayCurrency('MAD');
  const { has, roleKey, roleLabel } = usePermissions();
  const roleInfo = roleMeta(roleKey, roleLabel);
  const domainMonitor = useDomainMonitor({ userId: user?.id, userEmail: user?.email, mode: 'merchant' });

  const persistMenuOrder = useCallback(
    async (items: AppLink[]) => {
      if (!user) return;
      await Promise.all(
        items.map((item, index) =>
          supabase
            .from('appvendeur')
            .update({ menu_order: index + 1 })
            .eq('idcommerce', user.id)
            .eq('nompage', item.nompage)
        )
      ).catch(() => {});
    },
    [user]
  );

  const refreshInstalledApps = useCallback(() => {
    if (!user) return;
    supabase
      .from('appvendeur')
      .select('nompage, menu_order, id')
      .eq('idcommerce', user.id)
      .eq('status', 1)
      .eq('afficher_menu', 1)
      .then(({ data }) => {
        const rows = (data || []) as { nompage: string; menu_order: number | null; id: number }[];
        // Ordre défini par le marchand (menu_order), sinon ordre d'installation.
        // Un menu_order à 0 / nul = non défini → on retombe sur l'id (fin de liste).
        const orderOf = (r: { menu_order: number | null; id: number }) =>
          r.menu_order && r.menu_order > 0 ? r.menu_order : r.id;
        const sorted = [...rows].sort((a, b) => orderOf(a) - orderOf(b));
        setInstalledAppPages(new Set(sorted.map((r) => r.nompage)));
        setAppMenuItems(
          sorted.map((r) => {
            const meta = INSTALLABLE_APPS.find((a) => a.nompage === r.nompage);
            return {
              nompage: r.nompage,
              label: meta?.label || r.nompage,
              icon: meta?.icon || 'ri-apps-2-line',
            };
          })
        );
      })
      .catch(() => {});
  }, [user]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const visible = appMenuItems.filter((i) => has(requiredPermissionsForPath(`/dashboard/${i.nompage}`)));
      const oldIndex = visible.findIndex((i) => i.nompage === String(active.id));
      const newIndex = visible.findIndex((i) => i.nompage === String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      const nextVisible = arrayMove(visible, oldIndex, newIndex);
      // Réapplique l'ordre visible sur la liste complète (les apps masquées gardent leur position).
      const visibleIds = new Set(visible.map((v) => v.nompage));
      let cursor = 0;
      const next = appMenuItems.map((item) => (visibleIds.has(item.nompage) ? nextVisible[cursor++] : item));
      setAppMenuItems(next);
      void persistMenuOrder(next);
    },
    [appMenuItems, persistMenuOrder, has]
  );

  const fetchNotifications = useCallback(() => {
    if (!user) return;
    setNotificationsLoading(true);
    supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(30)
      .then(({ data, error }) => {
        if (!error) setNotifications((data || []) as Notification[]);
      })
      .catch(() => {})
      .finally(() => setNotificationsLoading(false));
  }, [user]);

  // Recherches IA clients (émises par l'assistant dans le Core) pour la cloche d'en-tête.
  const fetchAiNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const commerceId = getCommerceId(user);
      if (!commerceId) {
        setAiNotifications([]);
        setAiOrg(null);
        return;
      }
      const org = await getOrganizationByCommerce(commerceId);
      if (!org) {
        setAiNotifications([]);
        setAiOrg(null);
        return;
      }
      setAiOrg({ id: org.id, tenantId: org.tenant_id ?? null });
      const all = await listCoreNotifications(org.id);
      setAiNotifications(all.filter((n) => n.type === 'ai_customer_search'));
    } catch {
      setAiNotifications([]);
    }
  }, [user]);

  // Compteur de messages de contact non lus (badge sur l'app Service Client)
  const refreshContactBadge = useCallback(() => {
    if (!user) return;
    supabase
      .from('contact_messages')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('statusdemande', 0)
      .then(({ count }) => setUnreadContactCount(count || 0))
      .catch(() => {});
  }, [user]);

  // Compteur de nouvelles commandes (« En attente ») pour le badge du menu latéral.
  const refreshOrdersBadge = useCallback(() => {
    const commerceId = getCommerceId(user);
    if (!commerceId) {
      setNewOrdersCount(0);
      return;
    }
    supabase
      .from('commande')
      .select('id', { count: 'exact', head: true })
      .eq('idvendeur', commerceId)
      .eq('statut_com_vendeur', 'En attente')
      .then(({ count }) => setNewOrdersCount(count || 0))
      .catch(() => {});
  }, [user]);

  // Compteur de ruptures de stock (produits à 0) pour le badge « Ruptures » du menu latéral.
  const refreshStockBadge = useCallback(() => {
    const commerceId = getCommerceId(user);
    if (!commerceId) {
      setStockOutCount(0);
      return;
    }
    supabase
      .from('product_items')
      .select('id', { count: 'exact', head: true })
      .eq('idcommerce', commerceId)
      .lte('stock', 0)
      .then(({ count }) => setStockOutCount(count || 0))
      .catch(() => {});
  }, [user]);

  // Joue le son d'alerte « nouvelle commande » selon le choix du marchand
  // (carillon par défaut, fichier personnalisé ou silencieux — voir Paramètres).
  const playOrderChime = useCallback(() => {
    playOrderSound();
  }, []);

  // Affiche un toast + joue le son à chaque nouvelle commande.
  const showOrderToast = useCallback((row: Record<string, unknown>) => {
    const id = Number(row.id);
    const totalNum = Number(row.totalttc);
    setOrderToast({
      id: Number.isFinite(id) ? id : 0,
      client: String(row.nomclient || 'Client'),
      total: Number.isFinite(totalNum) ? totalNum.toLocaleString('fr-FR') : '',
      currency: String(row.monaie || user?.monaie || 'MAD'),
    });
    playOrderChime();
    if (orderToastTimer.current) window.clearTimeout(orderToastTimer.current);
    orderToastTimer.current = window.setTimeout(() => setOrderToast(null), 9000);
  }, [playOrderChime, user?.monaie]);

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    const fallback = buildSubdomain(user.user_name);
    setSiteUrl(fallback);
    setIsCustomDomain(false);

    supabase
      .from('websitedomain')
      .select('domaine')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.domaine) {
          const domain = data.domaine.replace(/\/+$/, '');
          setSiteUrl(domain.startsWith('http') ? domain : `https://${domain}`);
          setIsCustomDomain(true);
        }
      })
      .catch(() => {});

    refreshInstalledApps();
  }, [user, navigate, location.pathname, refreshInstalledApps]);

  // Re-synchronise le menu latéral quand le statut d'une app change (toggle dans /dashboard/apps)
  useEffect(() => {
    const handler = () => refreshInstalledApps();
    window.addEventListener('app-status-changed', handler);
    return () => window.removeEventListener('app-status-changed', handler);
  }, [refreshInstalledApps]);

  // Ferme les menus au clic en dehors
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (accountMenuRef.current && !accountMenuRef.current.contains(target)) {
        setAccountMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(target)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Ferme les menus à chaque changement de page
  useEffect(() => {
    setAccountMenuOpen(false);
    setNotificationsOpen(false);
  }, [location.pathname]);

  // Ouvre automatiquement la section qui contient la page consultée, pour que
  // l'élément actif soit toujours visible après un clic.
  useEffect(() => {
    const key = groupKeyForPath(location.pathname);
    if (!key) return;
    setOpenGroups((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }, [location.pathname]);

  // Charge les notifications quand l'utilisateur est disponible
  useEffect(() => {
    if (user) fetchNotifications();
  }, [user, fetchNotifications]);

  // Rafraîchit la cloche dès qu'un transfert / une action Zifek est effectué dans l'app.
  useEffect(() => {
    const handler = () => {
      if (user) fetchNotifications();
    };
    window.addEventListener('notifications-changed', handler);
    return () => window.removeEventListener('notifications-changed', handler);
  }, [user, fetchNotifications]);

  // Rafraîchissement temps réel des notifications entrantes (destinataire / commerçant).
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('notifications-bell')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          fetchNotifications();
          // 🔔 Son d'alerte pour les messages, réservations, formulaires et
          // alertes de stock (les commandes ont déjà leur propre toast + son).
          const type = (payload.new as { type?: string } | null)?.type;
          if (type && type !== 'order' && type !== 'payment') playOrderChime();
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, fetchNotifications, playOrderChime]);

  // Charge les recherches IA clients quand l'utilisateur est disponible
  useEffect(() => {
    if (user) fetchAiNotifications();
  }, [user, fetchAiNotifications]);

  // Compteur « mois gratuits » (offerts par la formule + parrainage), visible
  // directement dans l'en-tête sans ouvrir « Mon abonnement ».
  useEffect(() => {
    if (!user) {
      setFreeMonths(0);
      return;
    }
    let active = true;
    (async () => {
      let planMonths = 0;
      try {
        const { data: subs } = await supabase
          .from('package_subscriptions')
          .select('plan, status, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        const rows = (subs || []) as { plan: string | null; status: string | null }[];
        const current = rows.find((r) => (r.status || '').toLowerCase() === 'paid') || rows[0];
        if (current?.plan) {
          const { data: pkg } = await supabase
            .from('package')
            .select('mois_gratuits')
            .eq('nom', current.plan)
            .maybeSingle();
          planMonths = Number((pkg as { mois_gratuits?: number } | null)?.mois_gratuits) || 0;
        }
      } catch {
        planMonths = 0;
      }
      const referral = Number(user.parrainage_mois_gratuits) || 0;
      if (active) setFreeMonths(planMonths + referral);
    })();
    return () => { active = false; };
  }, [user]);

  // Rafraîchit le badge « messages de contact » à l'ouverture / navigation
  useEffect(() => {
    refreshContactBadge();
  }, [refreshContactBadge, location.pathname]);

  // Rafraîchit le badge quand le panneau des messages change un statut
  useEffect(() => {
    const handler = () => refreshContactBadge();
    window.addEventListener('contact-messages-changed', handler);
    return () => window.removeEventListener('contact-messages-changed', handler);
  }, [refreshContactBadge]);

  // Charge / rafraîchit le badge « nouvelles commandes ».
  useEffect(() => {
    refreshOrdersBadge();
  }, [refreshOrdersBadge, location.pathname]);

  // Rafraîchit immédiatement quand une commande est créée / mise à jour dans l'app.
  useEffect(() => {
    const handler = () => refreshOrdersBadge();
    window.addEventListener('orders-changed', handler);
    return () => window.removeEventListener('orders-changed', handler);
  }, [refreshOrdersBadge]);

  // Temps réel : nouvelles commandes et changements de statut.
  useEffect(() => {
    const commerceId = getCommerceId(user);
    if (!user || !commerceId) return;
    const channel = supabase
      .channel('orders-badge')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'commande', filter: `idvendeur=eq.${commerceId}` },
        (payload) => {
          refreshOrdersBadge();
          // Nouvelle commande → alerte visuelle (toast) + son.
          if (payload.eventType === 'INSERT') {
            showOrderToast(payload.new as Record<string, unknown>);
            fetchNotifications();
          }
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, refreshOrdersBadge, showOrderToast, fetchNotifications]);

  // Charge / rafraîchit le badge « Ruptures de stock ».
  useEffect(() => {
    refreshStockBadge();
  }, [refreshStockBadge, location.pathname]);

  // Rafraîchit immédiatement quand un mouvement de stock est enregistré dans l'app.
  useEffect(() => {
    const handler = () => refreshStockBadge();
    window.addEventListener('stock-changed', handler);
    return () => window.removeEventListener('stock-changed', handler);
  }, [refreshStockBadge]);

  // Temps réel : toute mise à jour du stock d'un produit met à jour le badge.
  useEffect(() => {
    const commerceId = getCommerceId(user);
    if (!user || !commerceId) return;
    const channel = supabase
      .channel('stock-badge')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'product_items', filter: `idcommerce=eq.${commerceId}` },
        () => refreshStockBadge()
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, refreshStockBadge]);

  // Les comptes « Développeurs » (typecompte 8) n'accèdent qu'à leur espace dédié.
  useEffect(() => {
    if (user && user.typecompte === 8) {
      navigate('/developpeur', { replace: true });
    }
  }, [user, navigate]);



  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('zifek-sidebar-collapsed', next ? '1' : '0');
      } catch {
        /* stockage indisponible */
      }
      return next;
    });
    setMobileMenuOpen(false);
  };

  // Permet à une page (ex. éditeur de bannières) de replier/déplier le menu.
  useEffect(() => {
    const handler = () => {
      setSidebarCollapsed((prev) => {
        const next = !prev;
        try {
          localStorage.setItem('zifek-sidebar-collapsed', next ? '1' : '0');
        } catch {
          /* stockage indisponible */
        }
        return next;
      });
    };
    window.addEventListener('zifek-toggle-sidebar', handler);
    return () => window.removeEventListener('zifek-toggle-sidebar', handler);
  }, []);

  // Scan d'un QR de compte depuis le dashboard du marchand.
  // - commerce  → page d'accès / profil commerçant
  // - client    → profil client (fiche unique)
  // - carnet de crédit → profil crédit du client
  const handleAccountScan = (raw: string) => {
    setScanOpen(false);
    const account = parseAccountQrData(raw.trim());
    if (!account) {
      setScanMsg('Code QR non reconnu.');
      setTimeout(() => setScanMsg(''), 3500);
      return;
    }
    if (account.type === 'merchant' || account.type === 'vendeur') {
      navigate(`/credit/join?merchant=${account.id}`);
      return;
    }
    if (account.type === 'customer') {
      navigate(`/dashboard/credit/customer/${account.id}`);
      return;
    }
    navigate(`/dashboard/customers-manager/${account.id}`);
  };

  const unreadCount = notifications.filter((n) => n.is_read === 0).length;
  const aiUnreadCount = aiNotifications.filter((n) => n.status !== 'read').length;
  const totalUnreadCount = unreadCount + aiUnreadCount;

  // Fait clignoter doucement le badge quand le nombre de non-lus augmente
  useEffect(() => {
    if (totalUnreadCount > prevUnreadRef.current) {
      setBadgeBlink(true);
      const timer = setTimeout(() => setBadgeBlink(false), 2000);
      prevUnreadRef.current = totalUnreadCount;
      return () => clearTimeout(timer);
    }
    prevUnreadRef.current = totalUnreadCount;
  }, [totalUnreadCount]);

  const markNotificationAsRead = (id: number) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)));
    supabase
      .from('notifications')
      .update({ is_read: 1 })
      .eq('id', id)
      .then(() => {});
  };

  // Clic sur une notification : marque comme lue ET ouvre l'entité concernée
  // (commande, message, contact, réservation...).
  const handleNotificationClick = (n: Notification) => {
    markNotificationAsRead(n.id);
    const target = notificationTarget(n);
    if (target) {
      setNotificationsOpen(false);
      navigate(target);
    }
  };

  const markAllNotificationsAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
    supabase
      .from('notifications')
      .update({ is_read: 1 })
      .eq('user_id', user?.id)
      .eq('is_read', 0)
      .then(() => {});
    // Recherches IA (Core) : marquées lues individuellement.
    aiNotifications.forEach((n) => {
      if (n.status !== 'read') markNotificationRead(n.id).catch(() => {});
    });
    setAiNotifications((prev) => prev.map((n) => ({ ...n, status: 'read' })));
  };

  // Ouvre la conversation IA complète du client lié à une recherche IA.
  const openAiThread = (n: CoreNotification) => {
    const clientId = Number(n.entity_id);
    if (!Number.isFinite(clientId)) return;
    setAiThread({ clientId, context: n.body || null });
    if (n.status !== 'read') {
      setAiNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, status: 'read' } : x)));
      markNotificationRead(n.id).catch(() => {});
    }
  };

  if (!user) {
    return null;
  }

  const isActive = (path: string) =>
    location.pathname === path ||
    (path === '/dashboard/finance' && location.pathname.startsWith('/dashboard/finance/'));

  // Éléments de base (hors apps épinglées), filtrés selon l'installation.
  // Pour un compte Restauration (typecompte 2), on insère l'espace « Restaurant »
  // juste après le tableau de bord.
  const isRestaurant = Number(user.typecompte) === RESTAURANT_TYPE_COMPTE;
  const isMarketplace = isMarketplaceAccount(user.type, user.typecompte);
  const navExtras: BaseNavItem[] = [];
  if (isRestaurant) navExtras.push(RESTAURANT_NAV_ITEM);
  if (isMarketplace) navExtras.push(MARKETPLACE_NAV_ITEM);
  const navItemsWithExtras: BaseNavItem[] = [BASE_NAV_ITEMS[0], ...navExtras, ...BASE_NAV_ITEMS.slice(1)];
  const baseFiltered = navItemsWithExtras.filter(
    (item) => (!item.appPage || installedAppPages.has(item.appPage)) && has(requiredPermissionsForPath(item.path))
  );

  // Apps épinglées filtrées selon les permissions du rôle connecté.
  const allowedApps = appMenuItems.filter((a) => has(requiredPermissionsForPath(`/dashboard/${a.nompage}`)));

  // Les apps épinglées s'affichent après « Produits », avant « Services ».
  const appsInsertIdx = (() => {
    const idx = baseFiltered.findIndex((item) => item.path === '/dashboard/services');
    return idx >= 0 ? idx : baseFiltered.length;
  })();
  const baseBeforeApps = baseFiltered.slice(0, appsInsertIdx);
  const baseAfterApps = baseFiltered.slice(appsInsertIdx);

  const navQuery = searchQuery.trim().toLowerCase();
  const baseSearchItems: { to: string; icon: string; label: string; tkey?: string }[] = baseFiltered.map((i) => ({
    to: i.path,
    icon: i.icon,
    label: i.label,
    tkey: i.tkey,
  }));
  const appSearchItems = allowedApps.map((a) => ({
    to: `/dashboard/${a.nompage}`,
    icon: a.icon,
    label: a.label,
    tkey: undefined as string | undefined,
  }));
  const filteredFlatItems = navQuery
    ? [...baseSearchItems, ...appSearchItems].filter((i) => i.label.toLowerCase().includes(navQuery))
    : [];

  const renderBaseLink = (item: BaseNavItem) => {
    const isStock = item.path === '/dashboard/gestion-stock';
    const badgeCount = item.path === '/dashboard/orders'
      ? newOrdersCount
      : isStock
        ? stockOutCount
        : 0;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setMobileMenuOpen(false)}
        className={`relative w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
          isActive(item.path)
            ? 'bg-primary-50 text-primary-700 font-semibold'
            : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
        }`}
      >
        {isActive(item.path) && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-primary-500"></span>
        )}
        <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center flex-shrink-0`}></i>
        <span className="leading-snug">{t(item.tkey || item.label, { defaultValue: item.label })}</span>
        {badgeCount > 0 && (
          <span
            title={isStock ? `${badgeCount} rupture(s) de stock` : undefined}
            className={`ml-auto inline-flex items-center gap-1 h-[18px] px-1.5 rounded-full text-white text-[10px] font-semibold leading-none ${isStock ? 'bg-red-500 animate-badge-blink' : 'min-w-[18px] justify-center bg-primary-500'}`}
          >
            {isStock && <i className="ri-error-warning-line text-[11px]"></i>}
            {isStock ? 'Ruptures ' : ''}{badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </Link>
    );
  };

  // Menu groupé : l'accueil reste seul en haut, les autres liens sont rangés
  // dans des sections dépliables (Vendre, Argent, Mon site…).
  const dashboardItem = baseFiltered.find((item) => item.path === '/dashboard') || null;
  const groupedBase = NAV_GROUPS
    .map((group) => ({
      ...group,
      items: baseFiltered.filter((item) => item.path !== '/dashboard' && group.paths.includes(item.path)),
    }))
    .filter((group) => group.items.length > 0);
  const dayActivityItem = baseFiltered.find((item) => item.path === '/dashboard/activite') || null;
  const ungroupedBase = baseFiltered.filter(
    (item) =>
      item.path !== '/dashboard' &&
      item.path !== '/dashboard/activite' &&
      !NAV_GROUPS.some((group) => group.paths.includes(item.path)),
  );
  const appsOpen = !!openGroups.apps;
  const hasActiveApp = allowedApps.some((app) => isActive(`/dashboard/${app.nompage}`));

  return (
    <div className="min-h-screen bg-background-100 flex overflow-x-hidden">
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside className={`fixed top-0 left-0 z-50 h-[100dvh] w-72 max-w-[85vw] bg-background-50 border-r border-background-200/70 transition-transform duration-200 lg:w-64 lg:max-w-none ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} ${sidebarCollapsed ? 'lg:-translate-x-full' : 'lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          {/* Logo - fixed at top */}
          <div className="flex-shrink-0 px-5 pt-4 pb-2">
            <div className="flex items-center justify-between gap-2 mb-2">
              <Link to="/" className="flex items-center gap-2 min-w-0">
                {brand.logo ? (
                  <div className="w-8 h-8 rounded-lg overflow-hidden bg-background-50 border border-background-200/50 flex items-center justify-center flex-shrink-0">
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="w-full h-full object-contain p-1"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm font-heading">
                      {brand.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="text-lg font-bold font-heading text-foreground-950 truncate">
                  {brand.name}
                </span>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="lg:hidden w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer shrink-0"
                title="Fermer le menu"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>

          {/* Recherche de page */}
          <div className="flex-shrink-0 px-3 pb-2">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher une page..."
                className="w-full pl-9 pr-8 py-2 rounded-md text-sm bg-background-100 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer"
                  title="Effacer"
                >
                  <i className="ri-close-circle-fill text-sm"></i>
                </button>
              )}
            </div>
          </div>

          {/* Navigation - scrollable */}
          <nav className="sidebar-scroll flex-1 overflow-y-auto px-3 pb-4 overscroll-contain" style={{ scrollbarWidth: 'thin', scrollbarColor: 'oklch(var(--foreground-200) / 0.5) transparent' }}>
            <div className="space-y-0.5">
              {navQuery ? (
                <>
                  {filteredFlatItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`relative w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                        isActive(item.to)
                          ? 'bg-primary-50 text-primary-700 font-semibold'
                          : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                      }`}
                    >
                      <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center flex-shrink-0`}></i>
                      <span className="leading-snug">{t(item.tkey || item.label, { defaultValue: item.label })}</span>
                    </Link>
                  ))}
                  {filteredFlatItems.length === 0 && (
                    <div className="px-3 py-8 text-center">
                      <i className="ri-search-eye-line text-2xl text-foreground-300"></i>
                      <p className="mt-2 text-sm text-foreground-500">Aucune page trouvée</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {dashboardItem && renderBaseLink(dashboardItem)}
                  {dayActivityItem && renderBaseLink(dayActivityItem)}
                  {groupedBase.map((group) => {
                    const open = !!openGroups[group.key];
                    const active = group.items.some((item) => isActive(item.path));
                    return (
                      <div key={group.key} className="pt-0.5">
                        <button
                          type="button"
                          onClick={() => toggleGroup(group.key)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                            active
                              ? 'text-primary-700 font-semibold'
                              : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                          }`}
                          aria-expanded={open}
                        >
                          <i className={`${group.icon} text-lg w-5 h-5 flex items-center justify-center flex-shrink-0`}></i>
                          <span className="leading-snug flex-1 text-left">{group.label}</span>
                          {active && <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0"></span>}
                          <i className={`ri-arrow-down-s-line text-base text-foreground-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}></i>
                        </button>
                        {open && (
                          <div className="mt-0.5 ml-4 pl-2 border-l border-background-200/70 space-y-0.5">
                            {group.items.map((item) => renderBaseLink(item))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {ungroupedBase.length > 0 && (
                    <div className="space-y-0.5 pt-0.5">
                      {ungroupedBase.map((item) => renderBaseLink(item))}
                    </div>
                  )}
                  {allowedApps.length > 0 && (
                    <div className="pt-0.5">
                      <button
                        type="button"
                        onClick={() => toggleGroup('apps')}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                          hasActiveApp
                            ? 'text-primary-700 font-semibold'
                            : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                        }`}
                        aria-expanded={appsOpen}
                      >
                        <i className="ri-pushpin-2-line text-lg w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                        <span className="leading-snug flex-1 text-left">Mes applications</span>
                        <span className="text-[10px] font-semibold text-foreground-400 flex-shrink-0">{allowedApps.length}</span>
                        <i className={`ri-arrow-down-s-line text-base text-foreground-400 transition-transform flex-shrink-0 ${appsOpen ? 'rotate-180' : ''}`}></i>
                      </button>
                      {appsOpen && (
                        <div className="mt-0.5 ml-4 pl-2 border-l border-background-200/70">
                          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={allowedApps.map((a) => a.nompage)} strategy={verticalListSortingStrategy}>
                              <div className="space-y-0.5">
                                {allowedApps.map((app) => (
                                  <SortableNavApp
                                    key={app.nompage}
                                    id={app.nompage}
                                    to={`/dashboard/${app.nompage}`}
                                    icon={app.icon}
                                    label={app.label}
                                    active={isActive(`/dashboard/${app.nompage}`)}
                                    badge={app.nompage === 'customer-support' ? unreadContactCount : 0}
                                    onNavigate={() => setMobileMenuOpen(false)}
                                  />
                                ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </nav>

          {/* User section - fixed at bottom */}
          <div className="flex-shrink-0 p-3 pt-2 border-t border-background-200/70 flex flex-col gap-0.5">
            {isSuperAdmin(user.typecompte) && (
              <Link
                to="/superadmin"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                title="Revenir à l'espace SuperAdmin"
              >
                <i className="ri-shield-keyhole-line text-lg"></i>
                <span>SuperAdmin</span>
              </Link>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              title="Déconnexion"
            >
              <i className="ri-logout-box-r-line text-lg"></i>
              <span>Déconnexion</span>
            </button>
            <div className="pt-1">
              <PlatformVersionBadge />
            </div>
          </div>
        </div>
      </aside>

      <div className={`flex-1 min-w-0 pb-16 lg:pb-0 transition-[margin] duration-200 ${sidebarCollapsed ? 'lg:ml-0' : 'lg:ml-64'}`}>
        <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
          <div className="flex items-center justify-between gap-2 h-14 md:h-16 px-3 sm:px-4 md:px-6 max-w-full min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 lg:flex-none">
              <button
                onClick={toggleSidebar}
                className="hidden lg:flex w-9 h-9 items-center justify-center rounded-md border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer shrink-0"
                title={sidebarCollapsed ? 'Afficher le menu' : 'Masquer le menu'}
                aria-label={sidebarCollapsed ? 'Afficher le menu' : 'Masquer le menu'}
              >
                <i className={sidebarCollapsed ? 'ri-menu-unfold-line text-lg' : 'ri-menu-fold-line text-lg'}></i>
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-200/70 cursor-pointer shrink-0"
              >
                <i className="ri-menu-line text-lg text-foreground-700"></i>
              </button>
              <h1 className="text-base sm:text-lg font-bold font-heading text-foreground-950 truncate">
                {user.nomcommerce}
              </h1>
              {roleLabel && (
                <span
                  className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${roleInfo.className}`}
                  title="Rôle associé à votre compte"
                >
                  <i className={roleInfo.icon}></i>
                  Accès : {roleInfo.label}
                </span>
              )}
              {freeMonths > 0 && (
                <Link
                  to="/dashboard/abonnement"
                  className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap flex-shrink-0 bg-accent-100 text-accent-800 hover:bg-accent-200/70 transition-colors cursor-pointer"
                  title="Mois gratuits offerts sur votre abonnement"
                >
                  <i className="ri-gift-2-line"></i>
                  {freeMonths} mois gratuit{freeMonths > 1 ? 's' : ''}
                </Link>
              )}
            </div>

            <div className="hidden lg:flex flex-1 justify-center px-4">
              <GlobalSearchBar className="w-full max-w-md" />
            </div>

            <div className="flex items-center gap-0.5 sm:gap-2 md:gap-3 shrink-0">
              <div className="relative pr-3 border-r border-background-200/70" ref={accountMenuRef}>
                <button
                  onClick={() => setAccountMenuOpen((v) => !v)}
                  className="group relative flex items-center gap-2.5 rounded-md px-1 py-1 -my-1 hover:bg-background-100 transition-colors cursor-pointer"
                  title="Mon compte"
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img src={avatarOr(user.image)} alt={user.name} className="w-full h-full object-cover" />
                    </div>
                    {totalUnreadCount > 0 && (
                      <span className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-primary-500 text-white text-[10px] font-semibold leading-none ring-2 ring-background-50 ${badgeBlink ? 'animate-badge-blink' : ''}`}>
                        {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 hidden md:block">
                    <div className="text-sm font-semibold text-foreground-950 truncate leading-tight text-left">{user.name}</div>
                    <div className="flex items-center gap-1 text-xs text-foreground-500 truncate max-w-[160px]">
                      <i className={isCustomDomain ? 'ri-link-m' : 'ri-global-line'}></i>
                      <span className="truncate">{cleanDisplayUrl(siteUrl)}</span>
                    </div>
                  </div>
                  <i className={`ri-arrow-down-s-line text-sm text-foreground-400 transition-transform hidden md:block ${accountMenuOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {accountMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-background-200/70 bg-background-100/50">
                      <div className="text-sm font-semibold text-foreground-950 truncate">{user.name}</div>
                      <div className="text-xs text-foreground-500 truncate mt-0.5">{user.email}</div>
                    </div>
                    <div className="p-1">
                      <Link
                        to="/dashboard/store"
                        onClick={() => setAccountMenuOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                      >
                        <i className="ri-user-3-line text-base w-5 h-5 flex items-center justify-center"></i>
                        <span>Mon profil</span>
                      </Link>
                      <Link
                        to="/dashboard/settings"
                        onClick={() => setAccountMenuOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                      >
                        <i className="ri-settings-3-line text-base w-5 h-5 flex items-center justify-center"></i>
                        <span>Paramètres</span>
                      </Link>
                      <button
                        onClick={() => { setAccountMenuOpen(false); setAccountQrOpen(true); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                      >
                        <i className="ri-qr-code-line text-base w-5 h-5 flex items-center justify-center"></i>
                        <span>Mon QR Zifek</span>
                      </button>
                      {!pwaInstalled && (
                        <button
                          onClick={() => {
                            setAccountMenuOpen(false);
                            if (pwaCanInstall) pwaInstall();
                            else setInstallInfoOpen(true);
                          }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-primary-700 hover:bg-primary-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-smartphone-line text-base w-5 h-5 flex items-center justify-center"></i>
                          <span>Installer l&apos;app</span>
                        </button>
                      )}
                    </div>
                    <div className="border-t border-background-200/70 p-1">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                      >
                        <i className="ri-logout-box-r-line text-base w-5 h-5 flex items-center justify-center"></i>
                        <span>Déconnexion</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="relative" ref={notificationsRef}>
                <button
                  onClick={() => {
                    setNotificationsOpen((v) => !v);
                    setAccountMenuOpen(false);
                  }}
                  className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
                  title="Notifications"
                >
                  <i className="ri-notification-3-line text-lg text-foreground-600"></i>
                  {totalUnreadCount > 0 && (
                    <span className={`absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-primary-500 text-white text-[10px] font-semibold leading-none ${badgeBlink ? 'animate-badge-blink' : ''}`}>
                      {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                    </span>
                  )}
                </button>

                {notificationsOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden z-50">
                    <div className="flex items-center justify-between px-4 h-12 border-b border-background-200/70">
                      <span className="text-sm font-semibold text-foreground-950">Notifications</span>
                      {totalUnreadCount > 0 && (
                        <button
                          onClick={markAllNotificationsAsRead}
                          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-check-double-line"></i>
                          <span>Tout marquer comme lu</span>
                        </button>
                      )}
                    </div>

                    <div className="max-h-[420px] overflow-y-auto">
                      {aiNotifications.length > 0 && (
                        <div className="border-b border-background-200/70 bg-accent-50/40">
                          <div className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-accent-700 flex items-center gap-1.5">
                            <i className="ri-robot-2-line"></i> Recherches IA clients
                          </div>
                          <div className="divide-y divide-accent-100">
                            {aiNotifications.slice(0, 6).map((n) => (
                              <button
                                key={`ai-${n.id}`}
                                onClick={() => openAiThread(n)}
                                className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors cursor-pointer ${n.status !== 'read' ? 'bg-accent-50/60 hover:bg-accent-100/60' : 'hover:bg-background-100'}`}
                              >
                                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-accent-100 text-accent-700">
                                  <i className="ri-robot-2-line"></i>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-foreground-800 leading-snug break-words">{n.title}</p>
                                    {n.created_at && Date.now() - new Date(n.created_at).getTime() < 24 * 3600 * 1000 && (
                                      <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full bg-accent-500 text-background-50 text-[10px] font-semibold whitespace-nowrap">Nouvelle</span>
                                    )}
                                  </div>
                                  {n.body && <p className="mt-0.5 text-xs text-foreground-500 line-clamp-2 break-words">{n.body}</p>}
                                  <p className="mt-1 text-[11px] text-accent-700 flex items-center gap-1"><i className="ri-chat-forward-line"></i> Ouvrir la conversation</p>
                                  <p className="mt-0.5 text-xs text-foreground-400">{n.created_at ? timeAgo(n.created_at) : ''}</p>
                                </div>
                                {n.status !== 'read' && <span className="w-2 h-2 rounded-full bg-accent-500 flex-shrink-0 mt-1.5"></span>}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {notificationsLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                          <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center">
                            <i className="ri-notification-off-line text-xl text-foreground-400"></i>
                          </div>
                          <p className="mt-3 text-sm font-medium text-foreground-700">Aucune notification</p>
                          <p className="mt-1 text-xs text-foreground-500">Vous serez informé ici des nouveautés et alertes.</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-background-200/70">
                          {notifications.map((n) => {
                            const meta = notifMeta(n.type);
                            return (
                              <button
                                key={n.id}
                                onClick={() => handleNotificationClick(n)}
                                className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors cursor-pointer ${
                                  n.is_read === 0 ? 'bg-primary-50/50 hover:bg-primary-50' : 'hover:bg-background-100'
                                }`}
                              >
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${meta.className}`}>
                                  <i className={`${meta.icon} text-base`}></i>
                                </div>
                                <div className="min-w-0 flex-1">
                                  {n.type === 'store_transfer' && n.is_read === 0 && (
                                    <span className="inline-flex items-center gap-1 mb-1 px-1.5 py-0.5 rounded-full bg-secondary-500 text-background-50 text-[10px] font-semibold whitespace-nowrap">
                                      <i className="ri-exchange-funds-line"></i>Nouveau transfert
                                    </span>
                                  )}
                                  {n.type !== 'store_transfer' && NOTIF_TYPE_LABEL[n.type] && (
                                    <span className={`inline-flex items-center gap-1 mb-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${meta.className}`}>
                                      <i className={meta.icon}></i>{NOTIF_TYPE_LABEL[n.type]}
                                    </span>
                                  )}
                                  <p className="text-sm text-foreground-800 leading-snug break-words">{appendCurrencyEquivalence(n.message, 'MAD', displayCurrency)}</p>
                                  <p className="mt-1 text-xs text-foreground-400">{timeAgo(n.created_at)}</p>
                                </div>
                                {n.is_read === 0 && (
                                  <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 mt-1.5"></span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="border-t border-background-200/70 px-4 py-2 flex justify-center">
                      <button
                        onClick={fetchNotifications}
                        className="flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-refresh-line"></i>
                        <span>Actualiser</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {isSuperAdmin(user.typecompte) && (
                <Link
                  to="/superadmin"
                  className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  title="Espace SuperAdmin"
                >
                  <i className="ri-shield-keyhole-line text-lg"></i>
                </Link>
              )}
              <LanguageSwitcher className="mr-1 hidden md:flex" />
              <CurrencySwitcher nativeCurrency={user.monaie || 'MAD'} className="mr-1 hidden md:flex" />
              {isSuperAdmin(user.typecompte) && (
                <Link
                  to="/superadmin"
                  className="hidden lg:flex items-center gap-1.5 px-2 py-1.5 md:px-3 rounded-full text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  title="Revenir à l'espace SuperAdmin"
                >
                  <i className="ri-shield-keyhole-line"></i>
                  <span className="hidden xl:inline">SuperAdmin</span>
                </Link>
              )}
              <button
                onClick={() => setPreviewOpen(!previewOpen)}
                className={`hidden md:flex items-center gap-1.5 px-2 py-1.5 md:px-3 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  previewOpen
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
                title="Prévisualiser le site"
              >
                <i className={previewOpen ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                <span className="hidden xl:inline">Prévisualiser</span>
              </button>
              <a
                href={siteUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden lg:flex items-center gap-1.5 px-2 py-1.5 md:px-3 rounded-full text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-global-line"></i>
                <span className="hidden xl:inline">Voir le site</span>
              </a>
            </div>
          </div>
        </header>

        <main className="min-w-0">
          {domainMonitor.alert && (
            <div className="px-4 md:px-6 pt-4">
              <DomainAlertBanner
                alert={domainMonitor.alert}
                to="/dashboard/settings?tab=domain"
                label="Configurer"
              />
            </div>
          )}
          <ErrorBoundary scope="Cette page">
            <PermissionGuard>
              <Outlet />
            </PermissionGuard>
          </ErrorBoundary>
        </main>

        {/* Preview Panel */}
        {previewOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-black/20 hidden lg:block"
              onClick={() => setPreviewOpen(false)}
            />

            {/* Panel */}
            <div className="fixed top-0 right-0 bottom-0 z-50 w-full lg:w-[480px] xl:w-[540px] bg-background-50 border-l border-background-200/70 shadow-lg transform transition-transform duration-300 ease-in-out translate-x-0">
              {/* Panel Header */}
              <div className="flex items-center justify-between px-4 h-14 border-b border-background-200/70 bg-background-50">
                <div className="flex items-center gap-2 min-w-0">
                  <i className="ri-eye-line text-sm text-foreground-500 flex-shrink-0"></i>
                  <span className="text-sm font-medium text-foreground-700 truncate">
                    Aperçu du site
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {/* Device size buttons */}
                  <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5">
                    {(Object.keys(DEVICE_SIZES) as PreviewDevice[]).map((device) => (
                      <button
                        key={device}
                        onClick={() => setPreviewDevice(device)}
                        className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                          previewDevice === device
                            ? 'bg-background-50 text-foreground-900 shadow-sm'
                            : 'text-foreground-400 hover:text-foreground-600'
                        }`}
                        title={DEVICE_SIZES[device].label}
                      >
                        <i className={`${DEVICE_SIZES[device].icon} text-sm`}></i>
                      </button>
                    ))}
                  </div>
                  <a
                    href={siteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                    title="Ouvrir dans un nouvel onglet"
                  >
                    <i className="ri-external-link-line text-sm text-foreground-500"></i>
                  </a>
                  <button
                    onClick={() => setPreviewOpen(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                    title="Fermer l'aperçu"
                  >
                    <i className="ri-close-line text-sm text-foreground-500"></i>
                  </button>
                </div>
              </div>

              {/* URL Bar */}
              <div className="px-3 py-2 bg-background-100 border-b border-background-200/70">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-background-50 rounded-md border border-background-200/70">
                  <i className="ri-lock-line text-xs text-accent-500 flex-shrink-0"></i>
                  <span className="text-xs text-foreground-600 truncate">{cleanDisplayUrl(siteUrl)}</span>
                </div>
              </div>

              {/* Iframe */}
              <div className="h-[calc(100vh-116px)] bg-background-200/30 flex items-start justify-center overflow-auto p-4">
                {siteUrl ? (
                  <div
                    className="bg-white transition-all duration-300 ease-in-out overflow-hidden flex-shrink-0"
                    style={{
                      width: DEVICE_SIZES[previewDevice].width,
                      height: previewDevice === 'desktop' ? '100%' : DEVICE_SIZES[previewDevice].height,
                      borderRadius: previewDevice !== 'desktop' ? '12px' : '0',
                      boxShadow: previewDevice !== 'desktop' ? '0 4px 24px rgba(0,0,0,0.12)' : 'none',
                    }}
                  >
                    <iframe
                      src={siteUrl}
                      className="w-full h-full border-0"
                      title="Aperçu du site"
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full w-full">
                    <p className="text-sm text-foreground-400">Aucune URL disponible</p>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile close button (outside panel) */}
            <button
              onClick={() => setPreviewOpen(false)}
              className="fixed top-4 right-4 z-50 w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 shadow lg:hidden"
            >
              <i className="ri-close-line text-foreground-600"></i>
            </button>
          </>
        )}
      </div>

      {/* Navigation mobile (barre inférieure) */}
      <MobileBottomNav
        items={[
          { icon: 'ri-dashboard-line', label: 'Tableau de bord', tkey: 'dash.dashboard', to: '/dashboard', active: isActive('/dashboard') },
          { icon: 'ri-shopping-bag-3-line', label: 'Produits', tkey: 'dash.products', to: '/dashboard/products', active: isActive('/dashboard/products') },
          { icon: 'ri-file-list-3-line', label: 'Commandes', tkey: 'dash.orders', to: '/dashboard/orders', active: isActive('/dashboard/orders'), badge: newOrdersCount },
          { icon: 'ri-user-line', label: 'Clients', tkey: 'dash.customers', to: '/dashboard/customers', active: isActive('/dashboard/customers') },
        ]}
        onQr={() => setQrChooserOpen(true)}
        onMore={() => setMobileMenuOpen(true)}
        moreLabel="Plus"
        hidden={previewOpen}
      />

      {/* Assistant commerce flottant (chat + note vocale + actions) */}
      <AssistantDock />

      {/* Choix QR : afficher mon code / scanner celui d'un client */}
      {qrChooserOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setQrChooserOpen(false)} />
          <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
            <div className="w-full sm:max-w-sm bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2"><i className="ri-qr-code-line text-primary-500"></i>Mon QR Zifek</h3>
                <button onClick={() => setQrChooserOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"><i className="ri-close-line text-foreground-500"></i></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => { setQrChooserOpen(false); setAccountQrOpen(true); }} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer">
                  <span className="w-11 h-11 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-qr-code-line text-xl"></i></span>
                  <span className="text-xs font-semibold text-foreground-800 text-center">Afficher mon QR</span>
                </button>
                <button onClick={() => { setQrChooserOpen(false); setScanOpen(true); }} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer">
                  <span className="w-11 h-11 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-scan-line text-xl"></i></span>
                  <span className="text-xs font-semibold text-foreground-800 text-center">Scanner un QR</span>
                </button>
              </div>
              <p className="mt-3 text-[11px] text-foreground-500 text-center leading-relaxed">Scannez le QR d&apos;un client pour ouvrir son profil, ou montrez le vôtre pour être identifié.</p>
            </div>
          </div>
        </>
      )}

      {scanOpen && (
        <QrScannerModal
          title="Scanner un QR Zifek"
          hint="Placez le code QR du client (ou d'un commerce) dans le cadre."
          onResult={handleAccountScan}
          onClose={() => setScanOpen(false)}
        />
      )}

      {scanMsg && !scanOpen && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-full bg-foreground-900 text-background-50 text-xs font-medium whitespace-nowrap">{scanMsg}</div>
      )}

      {/* Alerte « nouvelle commande » — toast + son (temps réel) */}
      {orderToast && (
        <div className="fixed top-16 right-3 sm:right-5 z-[55] w-[calc(100vw-1.5rem)] max-w-sm">
          <div className="bg-background-50 border border-primary-200/70 rounded-xl overflow-hidden animate-card-in">
            <div className="flex items-start gap-3 p-4">
              <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-shopping-cart-2-line text-background-50 text-lg"></i>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-foreground-950 flex items-center gap-1.5">
                  <i className="ri-notification-3-line text-primary-600"></i>
                  Nouvelle commande !
                </p>
                <p className="text-xs text-foreground-600 mt-0.5 truncate">
                  {orderToast.client}{orderToast.total ? ` · ${orderToast.total} ${orderToast.currency}` : ''}
                </p>
                {orderToast.id > 0 && <p className="text-[11px] text-foreground-400 mt-0.5">Commande #{orderToast.id}</p>}
              </div>
              <button
                onClick={() => setOrderToast(null)}
                className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
                title="Fermer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="px-4 pb-4">
              <button
                onClick={() => {
                  navigate(orderToast.id > 0 ? `/dashboard/orders?order=${orderToast.id}` : '/dashboard/orders');
                  setOrderToast(null);
                }}
                className="w-full flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-eye-line"></i>
                Voir la commande
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR de compte (unique, lié à l'id en base) */}
      {accountQrOpen && user && (
        <AccountQrModal
          title="Mon QR Zifek"
          subtitle={`${user.nomcommerce || user.name} · ${user.email}`}
          data={buildAccountQrData(Number(user.typecompte) === TYPE_COMPTE_CLIENT ? 'client' : 'merchant', user.id)}
          onScan={() => { setAccountQrOpen(false); setScanOpen(true); }}
          onClose={() => setAccountQrOpen(false)}
        />
      )}

      {/* Conversation IA complète d'un client (ouverte depuis la cloche) */}
      {aiThread && aiOrg && (
        <AiClientThreadModal
          organizationId={aiOrg.id}
          tenantId={aiOrg.tenantId}
          clientId={aiThread.clientId}
          contextLabel={aiThread.context}
          onClose={() => setAiThread(null)}
          onReplied={fetchAiNotifications}
        />
      )}

      {/* Instructions d'installation (quand le prompt natif n'est pas dispo) */}
      {installInfoOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[60]" onClick={() => setInstallInfoOpen(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-sm bg-background-50 rounded-2xl border border-background-200/70 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-smartphone-line text-xl"></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground-950">Installer l&apos;application</h3>
                <p className="text-xs text-foreground-500 mt-0.5">Utilisez le dashboard même sans internet.</p>
              </div>
            </div>
            <div className="space-y-3 text-sm text-foreground-700">
              <div className="flex items-start gap-2.5">
                <i className="ri-android-line text-lg text-accent-600 mt-0.5"></i>
                <p><strong>Android :</strong> ouvrez le menu du navigateur (⋮) puis <strong>« Installer l&apos;application »</strong> / <strong>« Ajouter à l&apos;écran d&apos;accueil »</strong>.</p>
              </div>
              <div className="flex items-start gap-2.5">
                <i className="ri-apple-line text-lg text-accent-600 mt-0.5"></i>
                <p><strong>iPhone (Safari) :</strong> appuyez sur <strong>Partager</strong> puis <strong>« Sur l&apos;écran d&apos;accueil »</strong>.</p>
              </div>
            </div>
            <button
              onClick={() => setInstallInfoOpen(false)}
              className="mt-5 w-full h-11 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Compris
            </button>
          </div>
        </>
      )}
    </div>
  );
}